import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { getIntelCohort } from '../services/api';
import { trackEvent } from '../services/analytics';
import { useIndustrySession } from '../services/industryAccess';
import { slugifyTitle } from '../services/titleLinks';

const IndustryPreviewPage = () => {
  const session = useIndustrySession();
  const [cohort, setCohort] = React.useState([]);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!session.allowed) return;
    getIntelCohort()
      .then((titles) => setCohort(titles || []))
      .catch((reason) => setError(reason));
  }, [session.allowed]);

  React.useEffect(() => {
    if (session.allowed) trackEvent('industry_workspace_viewed', { cohort: 'dark_launch_50' });
  }, [session.allowed]);

  if (session.loading) return <main className="min-h-screen bg-binge-bg text-binge-sub flex items-center justify-center">Checking industry access…</main>;

  if (!session.user) return (
    <main className="min-h-screen bg-binge-bg text-binge-ink flex items-center justify-center px-5">
      <div className="max-w-lg text-center"><p className="text-xs uppercase tracking-[0.2em] text-binge-gold">Industry Intelligence</p><h1 className="font-serif text-4xl mt-3">Sign in first</h1><p className="text-binge-sub mt-4">Use the approved Google account, then return to this page.</p><Link to="/" className="inline-block mt-6 text-binge-gold">Return to sign in →</Link></div>
    </main>
  );

  if (!session.allowed) return (
    <main className="min-h-screen bg-binge-bg text-binge-ink flex items-center justify-center px-5">
      <div className="max-w-lg text-center"><p className="text-xs uppercase tracking-[0.2em] text-binge-gold">Industry Intelligence</p><h1 className="font-serif text-4xl mt-3">This account does not have preview access</h1><p className="text-binge-sub mt-4">Signed in as {session.user.email}. Internal preview access is currently allowlisted.</p><Link to="/profile" className="inline-block mt-6 text-binge-gold">Return to profile →</Link></div>
    </main>
  );

  const movies = cohort.filter((item) => item.media_type === 'movie');
  const series = cohort.filter((item) => item.media_type === 'series');
  const titleList = (items) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
      {items.map((item) => (
        <Link
          key={`${item.media_type}-${item.tmdb_id}`}
          to={`/title/${item.media_type}/${item.tmdb_id}/${slugifyTitle(item.title)}#industry-lens`}
          onClick={() => trackEvent('industry_title_opened', { title_key: `tmdb:${item.media_type}:${item.tmdb_id}` })}
          className="rounded-xl border border-white/10 bg-white/[0.035] px-4 py-4 hover:border-binge-gold/60 transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.15em] text-binge-faint">{item.media_type} · TMDB {item.tmdb_id}</span>
          <strong className="block font-serif text-lg text-binge-cream mt-1">{item.title}</strong>
          <small className="text-binge-gold">Open intelligence →</small>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-binge-bg text-binge-ink flex flex-col">
      <header className="flex items-center justify-between px-5 sm:px-10 py-4 border-b border-white/10">
        <Link to="/" className="font-serif text-lg">What to Binge</Link>
        <nav className="flex items-center gap-5 text-sm"><Link to="/profile" className="text-binge-sub hover:text-binge-ink">Profile</Link><span className="text-binge-gold">Industry Preview</span></nav>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-[0.22em] text-binge-gold">BingeIQ · Internal industry access</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">Title intelligence workspace</h1>
        <p className="text-binge-sub max-w-3xl mt-4 leading-relaxed">This is the industry access point for intelligence already developed: reviewed Content DNA, Audience DNA, grounded synopsis and briefs, explained semantic neighbours, observed availability, public-interest momentum, regional evidence limits, and the neutral Industry Lens.</p>

        <div className="grid md:grid-cols-3 gap-4 mt-9">
          <article className="rounded-2xl border border-binge-gold/30 bg-binge-gold/[0.07] p-5"><span className="text-xs uppercase tracking-widest text-binge-gold">Coverage</span><strong className="block font-serif text-3xl mt-3">50 titles</strong><p className="text-sm text-binge-sub mt-2">All have independently reviewed taxonomy-v2 enrichment.</p></article>
          <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5"><span className="text-xs uppercase tracking-widest text-binge-faint">Signals</span><strong className="block font-serif text-3xl mt-3">Daily</strong><p className="text-sm text-binge-sub mt-2">Wikimedia, YouTube, catalog and provider observations accumulate over time.</p></article>
          <article className="rounded-2xl border border-white/10 bg-white/[0.035] p-5"><span className="text-xs uppercase tracking-widest text-binge-faint">Strategic fit</span><strong className="block font-serif text-3xl mt-3">Not inferred</strong><p className="text-sm text-binge-sub mt-2">Requires a real platform profile, catalog, audience, territories and strategy.</p></article>
        </div>

        <section className="mt-12 rounded-2xl border border-white/10 p-5 sm:p-7">
          <h2 className="font-serif text-2xl">What you can inspect now</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 text-sm text-binge-sub">
            {['Creative and emotional fingerprint', 'Audience affinity and viewing context', 'Similarity reasons and differences', 'Observed provider availability', 'Historical attention momentum', 'Territory evidence and limitations', 'Availability density and audience promise', 'Source coverage and methodology'].map((item) => <div key={item} className="border-l border-binge-gold/40 pl-3 py-1">{item}</div>)}
          </div>
        </section>

        {error ? <p className="mt-10 text-red-300">The cohort could not be loaded. Please retry shortly.</p> : <>
          <section className="mt-12"><div className="flex items-end justify-between"><div><p className="text-xs uppercase tracking-widest text-binge-faint">Cohort</p><h2 className="font-serif text-3xl mt-1">Films</h2></div><span className="text-binge-faint">{movies.length}</span></div>{titleList(movies)}</section>
          <section className="mt-12"><div className="flex items-end justify-between"><div><p className="text-xs uppercase tracking-widest text-binge-faint">Cohort</p><h2 className="font-serif text-3xl mt-1">Series</h2></div><span className="text-binge-faint">{series.length}</span></div>{titleList(series)}</section>
        </>}

        <section className="mt-14 border-t border-white/10 pt-7 text-sm text-binge-sub"><strong className="text-binge-cream">Current boundary</strong><p className="mt-2 max-w-3xl">This preview does not provide acquisition recommendations, rights availability, predicted views, revenue, subscriber impact, or company-specific fit. Those require platform context and additional approved data sources.</p></section>
      </main>
      <Footer />
    </div>
  );
};

export default IndustryPreviewPage;
